import { useState, useEffect } from "react";
import Loader from "components/Loader";
import HelpCard from "components/Cards/HelpCard";
import { Button, Col, Form } from "react-bootstrap";
import Validator from "utils/Validator";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import { getInputFieldStyle, getButtonStyle, getTextareaStyle } from "utils/formStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";
import { submitAiOverviewByContentRequest } from "services/buddyXApiHubService";
import AiOverviewTable from "components/MarkdownRender/tables/AiOverviewTable";

export default function Page() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [text, setText] = useState("");
  const [noQueries, setNoQueries] = useState(10);
  const [aiOverview, setAiOverview] = useState("");
  const [aiOverviewList, setAiOverviewList] = useState([]);
  const [isListResponse, setIsListResponse] = useState(false);
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [copiedRowIndex, setCopiedRowIndex] = useState(null);
  const [errors, setErrors] = useState({
    text: "",
    noQueries: "",
  });

  const firstLevelTopic = "Content Editor";
  const secondLevelTopic = "AI Overview";
  const thirdLevelTopic = "Content";

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "text") {
      setText(value);
    } else if (name === "noQueries") {
      setNoQueries(parseInt(value) || 1);
    }
  };

  const getWordCount = (text) => {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    let error = "";
    if (name === "text") {
      const wordCount = getWordCount(value);
      if (!value.trim()) {
        error = "Please enter some text content";
      } else if (wordCount < 10) {
        error = "Text content should be at least 10 words long";
      } else if (wordCount > 5000) {
        error = "Text content should not exceed 5,000 words";
      }
    } else if (name === "noQueries") {
      const num = parseInt(value);
      if (!num || num < 1 || num > 50) {
        error = "Number of queries must be between 1 and 50";
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleSubmit = async () => {
    if (!text.trim() || isLoading) return;

    setIsLoading(true);
    setIsResultGot(false);
    setAiOverview("");
    setErrorMessage("");

    try {
      const response = await submitAiOverviewByContentRequest(
        { "text": text, no_queries: noQueries },
        "ai_overview_content"
      );

      setAiOverviewList(response);
      setIsListResponse(true);
      setAiOverview("");
      setIsResultGot(true);
    } catch (error) {
      console.error("Error generating AI overview:", error);
      if (error.response?.status === 402) {
        handleInsufficientCredits(error.response.data);
      } else {
        setErrorMessage(
          error.response?.data?.detail ||
            "Failed to generate AI overview. Please try again."
        );
        setShowErrorNotification(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setText("");
    setNoQueries(10);
    setAiOverview("");
    setAiOverviewList([]);
    setIsListResponse(false);
    setIsResultGot(false);
    setErrors({ text: "", noQueries: "" });
    setShowCopyNotification(false);
    setShowErrorNotification(false);
    setErrorMessage("");
    setCopiedRowIndex(null);
  };

  const handleCopy = async () => {
    try {
      const textToCopy = isListResponse
        ? aiOverviewList
            .map((item, index) => {
              if (typeof item === "object" && item !== null && !Array.isArray(item)) {
                const entries = Object.entries(item);
                const qaText = entries.map(([question, answer]) => `Question: ${question}\nAnswer: ${answer}`).join('\n\n');
                return `${index + 1}. ${qaText}`;
              } else if (typeof item === "string") {
                return `${index + 1}. ${item}`;
              } else {
                return `${index + 1}. ${JSON.stringify(item)}`;
              }
            })
            .join("\n\n")
        : aiOverview;
      await navigator.clipboard.writeText(textToCopy);
      setShowCopyNotification(true);
    } catch (err) {
      console.error("Failed to copy content: ", err);
    }
  };

  const handleRowCopy = async (content, index) => {
    try {
      let textToCopy;
      if (typeof content === "object" && content !== null && !Array.isArray(content)) {
        // Handle object with question as key and answer as value
        const entries = Object.entries(content);
        textToCopy = entries.map(([question, answer]) => `Question: ${question}\n\nAnswer: ${answer}`).join('\n\n---\n\n');
      } else if (typeof content === "string") {
        textToCopy = content;
      } else {
        textToCopy = JSON.stringify(content, null, 2);
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setCopiedRowIndex(index);
      setShowCopyNotification(true);

      // Reset the copied row indicator after 2 seconds
      setTimeout(() => {
        setCopiedRowIndex(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy row content: ", err);
    }
  };

  // Form validation
  useEffect(() => {
    const wordCount = getWordCount(text);
    const isValid = text.trim() !== "" && wordCount >= 10 && wordCount <= 5000 && !errors.text && !errors.noQueries;

    setIsFormValid(isValid);
  }, [text, noQueries, errors.text, errors.noQueries]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showCopyNotification}
        message="AI Overview copied!"
        type="success"
        icon="fas fa-check-circle"
        duration={2000}
        onClose={() => setShowCopyNotification(false)}
        position="top-right"
      />

      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />

      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secondLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / <b>{secondLevelTopic}</b> / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Generate comprehensive <b>AI-powered content overviews</b> by analyzing
        any text content. Get detailed insights, strategic recommendations, and
        actionable steps to improve your content's effectiveness and search
        visibility.
      </p>

      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={
              "Content Analysis generates comprehensive AI-powered overviews by analyzing any text content. Paste your blog post, article, or any text content to get detailed insights, content strategy recommendations, and actionable steps to improve your content's performance and search visibility."
            }
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">Generate AI Overview from Content</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Paste your text content below, specify the number of queries, and get comprehensive insights.
            </p>
            <Form.Group>
              <div className="d-flex flex-column mt-2">
                <div className="mb-3">
                  <Form.Control
                    as="textarea"
                    rows={8}
                    name="text"
                    placeholder="Paste your text content here... (minimum 10 words, maximum 5,000 words)"
                    value={text}
                    onChange={handleInputChange}
                    isInvalid={!!errors.text}
                    onBlur={handleBlur}
                    required
                    className="px-2"
                    style={{ ...getTextareaStyle('type6'), resize: "vertical", minHeight: "200px" }}
                  />
                  {errors.text && (
                    <div className="text-danger small mt-1">{errors.text}</div>
                  )}
                  <div className="text-muted small mt-1">
                    Words: {getWordCount(text)}/5,000
                  </div>
                </div>
                <div className="d-flex align-items-end">
                  <Col sm={3} className="me-2">
                    <Form.Label className="small fw-bold">Number of Queries</Form.Label>
                    <Form.Control
                      type="number"
                      name="noQueries"
                      placeholder="Number of queries"
                      value={noQueries}
                      onChange={handleInputChange}
                      isInvalid={!!errors.noQueries}
                      onBlur={handleBlur}
                      min="1"
                      max="50"
                      className="px-2"
                      style={getInputFieldStyle('type6')}
                    />
                    {errors.noQueries && (
                      <div className="text-danger small mt-1">
                        {errors.noQueries}
                      </div>
                    )}
                  </Col>
                  <div className="col-lg-4 col-md-4 col-sm-12 mb-2 me-1">
                    <div className="d-flex justify-content-start align-items-end">
                      <Button
                        variant="dark"
                        className="me-1"
                        onClick={handleSubmit}
                        disabled={!isFormValid || isLoading}
                        style={getButtonStyle('type6', 'primary')}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Analyzing...
                          </>
                        ) : (
                          "Analyze Content"
                        )}
                      </Button>
                      <Button
                        variant="outline-dark"
                        className="me-1"
                        onClick={handleReset}
                        disabled={isLoading}
                        style={getButtonStyle('type6', 'outline')}
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {text && isResultGot && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="d-flex fw-bold mb-0">
              Content analysis for
              <span className="search-item">&nbsp;"{text.substring(0, 50)}..."</span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}

        {/* Table Display for List Responses */}
        {isResultGot && isListResponse && aiOverviewList.length > 0 && (
          <AiOverviewTable
            result={aiOverviewList}
            title="Generated AI Overview"
            onCopy={() => setShowCopyNotification(true)}
          />
        )}
      </div>

      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
    </div>
  );
}
